
const DJANGO_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const ACC_BASE = `${DJANGO_BASE_URL}/accompagnement`;
const CMS_BASE = `${DJANGO_BASE_URL}/landing/cms`;

/* ═══════════════════════════════════════════════════════════════════════
   DIRE CE QUI S'EST PASSÉ, PAS LE CODE

   « Erreur Serveur (404) » n'apprend rien à qui ne connaît pas le
   protocole HTTP — et n'apprend rien non plus à qui le connaît, car le
   code ne dit pas QUOI était introuvable. Chaque statut reçoit donc une
   phrase, écrite du point de vue de la personne : ce qu'elle voulait
   faire, pourquoi ça n'a pas marché, et le cas échéant quoi faire.

   Le code reste attaché à l'exception (`erreur.statut`) pour le code qui
   en a besoin ; il n'apparaît simplement plus à l'écran.
   ═══════════════════════════════════════════════════════════════════════ */
const PHRASES = {
  400: "Les informations envoyées ne sont pas valables. Vérifiez les champs signalés.",
  401: "Votre session a expiré. Reconnectez-vous pour continuer.",
  403: "Vous n'avez pas accès à cette partie de l'administration.",
  404: "Page introuvable. L'élément demandé n'existe plus ou a été supprimé.",
  405: "Cette action n'est pas possible ici.",
  408: "Le serveur a mis trop de temps à répondre. Réessayez.",
  409: "Cet élément a été modifié entre-temps. Rechargez la page avant de réessayer.",
  413: "Le fichier est trop volumineux.",
  415: "Ce format de fichier n'est pas accepté.",
  429: "Trop de tentatives d'affilée. Patientez un instant avant de réessayer.",
  500: "Le serveur a rencontré un problème. Ce n'est pas de votre fait — réessayez dans un moment.",
  502: "Le serveur est momentanément injoignable. Réessayez dans un moment.",
  503: "Le service est indisponible pour l'instant. Réessayez dans quelques minutes.",
  504: "Le serveur a mis trop de temps à répondre. Réessayez dans un moment.",
};

/* Un incident qui touche TOUTE l'application -- serveur injoignable,
   panne -- ne se dit pas dans le coin d'un formulaire. On le signale une
   fois, en haut, et on le retire dès que ça remarche. Les erreurs
   ordinaires (champ invalide, droit manquant) ne passent pas par là :
   elles appartiennent à l'écran qui les provoque. */
export const INCIDENT = 'assopro:incident';

const signalerIncident = (message) => {
  window.dispatchEvent(new CustomEvent(INCIDENT, { detail: { message } }));
};

const signalerRetour = () => {
  window.dispatchEvent(new CustomEvent(INCIDENT, { detail: { message: null } }));
};

/* La session ne se renouvelle plus : la fenêtre « Session expirée » s'ouvre
   (système d'états, `src/etats/Systeme.jsx`) et propose de se reconnecter
   en revenant sur la même page. Avant, chaque écran affichait « Session
   expirée » dans son coin, et rien ne ramenait à la connexion. */
const signalerSessionExpiree = () => {
  window.dispatchEvent(new CustomEvent('assopro:session-expiree'));
};

/* Trop de requêtes (429) : un toast le dit une fois ; l'écran garde ce
   qu'il affiche. */
const signalerLimite = () => {
  window.dispatchEvent(new CustomEvent('assopro:limite'));
};

export const phraseDErreur = (statut) => PHRASES[statut]
  || (statut >= 500
    ? "Le serveur a rencontré un problème. Réessayez dans un moment."
    : "L'opération n'a pas abouti. Réessayez.");

const handleResponse = async (res) => {
  if (res.status === 204) return true;
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    // Un message du serveur est toujours préférable : il sait, lui, de
    // quoi il parle. La phrase générique ne sert qu'à défaut.
    const erreur = new Error(errorData.error || errorData.detail
                             || phraseDErreur(res.status));
    // Le corps d'une reponse 400 de DRF est un dictionnaire
    // { champ: ["message"] }. Le jeter avec l'exception permet aux formulaires
    // d'afficher l'erreur SOUS le champ fautif, au lieu du laconique
    // « Erreur Serveur (400) » qui n'indique pas quoi corriger.
    erreur.statut = res.status;
    erreur.details = errorData;
    throw erreur;
  }
  return res.json();
};

const authHeaders = () => {
  const token = localStorage.getItem('asso_acc_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Rafraichit l'access token (duree de vie 1h) a partir du refresh token (7j).
// Sans cela, la session admin expirait silencieusement au bout d'une heure.
let refreshInFlight = null;
const refreshAccessToken = () => {
  if (refreshInFlight) return refreshInFlight;           // une seule tentative concurrente
  const refresh = localStorage.getItem('asso_acc_refresh');
  if (!refresh) return Promise.resolve(false);
  refreshInFlight = fetch(`${ACC_BASE}/auth/token/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (!data?.access) return false;
      localStorage.setItem('asso_acc_token', data.access);
      if (data.refresh) localStorage.setItem('asso_acc_refresh', data.refresh); // rotation activee
      return true;
    })
    .catch(() => false)
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
};

const authFetch = async (url, options = {}) => {
  // Un corps FormData (televersement d'image) doit laisser le navigateur poser
  // lui-meme `Content-Type: multipart/form-data; boundary=...`. Imposer
  // `application/json` ici rendait toute image intransmissible : le serveur
  // recevait un corps qu'il ne savait pas decouper.
  const estFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const send = () => fetch(url, {
    ...options,
    headers: {
      ...(estFormData ? {} : { 'Content-Type': 'application/json' }),
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });

  let res;
  try {
    res = await send();
  } catch (reseau) {
    // `fetch` ne rejette que si la requête n'est jamais partie ou n'est
    // jamais revenue : pas de réseau, serveur éteint, CORS. Le statut
    // n'existe pas dans ce cas -- d'où un message à part.
    signalerIncident("Le serveur ne répond pas. Vérifiez votre connexion ; "
                     + "la page se remettra à jour dès qu'il répondra.");
    throw new Error("Le serveur ne répond pas.");
  }

  if (res.status >= 500) {
    signalerIncident(phraseDErreur(res.status));
  } else {
    signalerRetour();
  }

  if (res.status === 401) {
    const renewed = await refreshAccessToken();
    if (renewed) {
      res = await send();                                // nouvelle tentative avec le jeton frais
    }
    if (res.status === 401) {
      localStorage.removeItem('asso_acc_token');
      localStorage.removeItem('asso_acc_refresh');
      signalerSessionExpiree();
      const expiree = new Error('Session expirée. Veuillez vous reconnecter.');
      expiree.statut = 401;
      throw expiree;
    }
  }
  if (res.status === 429) signalerLimite();

  return handleResponse(res);
};

/* Les fichiers privés — aperçu signé, image de sa propre signature — ne
   s'atteignent pas avec un `<img src>` ni un `<a href>` : le serveur
   exige l'en-tête `Authorization`, qu'une balise n'envoie pas. On les
   récupère donc en blob, et l'écran en fait une `URL.createObjectURL`.

   Même traitement des erreurs que `authFetch`, `statut` compris : un 503
   (moteur de rendu absent) doit pouvoir se distinguer d'un 403. */
const authFetchBlob = async (url) => {
  let res;
  try {
    res = await fetch(url, { headers: authHeaders() });
  } catch (reseau) {
    signalerIncident("Le serveur ne répond pas.");
    throw new Error("Le serveur ne répond pas.");
  }

  if (res.status === 401) {
    if (await refreshAccessToken()) {
      res = await fetch(url, { headers: authHeaders() });
    }
    if (res.status === 401) signalerSessionExpiree();
  }
  if (res.status === 429) signalerLimite();
  if (!res.ok) {
    const corps = await res.json().catch(() => ({}));
    const erreur = new Error(corps.error || corps.detail || phraseDErreur(res.status));
    erreur.statut = res.status;
    throw erreur;
  }
  return res.blob();
};

export const adminApi = {
  // --- AUTH ---
  login: (credentials) => fetch(`${DJANGO_BASE_URL}/users/login/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials)
  }).then(handleResponse).then(data => {
    if (data.access) localStorage.setItem('asso_acc_token', data.access);
    if (data.refresh) localStorage.setItem('asso_acc_refresh', data.refresh);
    return data;
  }),

  getThemePreference: (userId) => localStorage.getItem(`admin_theme_${userId}`) || 'light',
  setThemePreference: (userId, theme) => localStorage.setItem(`admin_theme_${userId}`, theme),

  // --- PARCOURS D'ACCÈS ---------------------------------------------------
  // Ces appels partent AVANT toute session : ils utilisent `fetch` nu, pas
  // `authFetch`, qui tenterait de rafraîchir un jeton inexistant.
  verifierLien: (jeton) =>
    fetch(`${DJANGO_BASE_URL}/users/activation/verifier/?jeton=${encodeURIComponent(jeton)}`)
      .then(handleResponse),

  definirMotDePasse: (donnees) => fetch(`${DJANGO_BASE_URL}/users/activation/definir/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(donnees),
  }).then(handleResponse).then((data) => {
    // La définition du mot de passe ouvre directement la session : demander
    // de se reconnecter juste après aurait été une étape de plus sans raison.
    if (data.access) localStorage.setItem('asso_acc_token', data.access);
    if (data.refresh) localStorage.setItem('asso_acc_refresh', data.refresh);
    return data;
  }),

  demanderCode: (identifiant) => fetch(`${DJANGO_BASE_URL}/users/mot-de-passe/code/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifiant }),
  }).then(handleResponse),

  verifierCode: (identifiant, code) => fetch(`${DJANGO_BASE_URL}/users/mot-de-passe/verifier/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifiant, code }),
  }).then(handleResponse),

  getMesIdentifiants: () => authFetch(`${DJANGO_BASE_URL}/users/me/identifiants/`),
  majMesIdentifiants: (donnees) => authFetch(`${DJANGO_BASE_URL}/users/me/identifiants/`, {
    method: 'PATCH', body: JSON.stringify(donnees),
  }),

  // --- NOTIFICATIONS ------------------------------------------------------
  getNotifications: (nonLues = false) =>
    authFetch(`${DJANGO_BASE_URL}/notifications/${nonLues ? '?non_lues=1' : ''}`),
  marquerNotificationsLues: (ids) => authFetch(`${DJANGO_BASE_URL}/notifications/lire/`, {
    method: 'POST', body: JSON.stringify(ids ? { ids } : {}),
  }),
  getPreferencesNotification: () => authFetch(`${DJANGO_BASE_URL}/notifications/preferences/`),
  majPreferenceNotification: (donnees) => authFetch(`${DJANGO_BASE_URL}/notifications/preferences/`, {
    method: 'PATCH', body: JSON.stringify(donnees),
  }),

  // --- CANDIDATURES -------------------------------------------------------
  getCandidatures: (filtres = {}) => {
    const requete = new URLSearchParams(
      Object.entries(filtres).filter(([, v]) => v)).toString();
    return authFetch(`${ACC_BASE}/candidatures/${requete ? `?${requete}` : ''}`);
  },
  getCandidature: (id) => authFetch(`${ACC_BASE}/candidatures/${id}/`),
  // Le CV, pour l'afficher dans le dossier : /media/ n'est pas lisible d'ici.
  cvCandidature: (id) => authFetchBlob(`${ACC_BASE}/candidatures/${id}/cv/`),
  ecrireCandidat: (id, sujet, message) => authFetch(`${ACC_BASE}/candidatures/${id}/messages/`, {
    method: 'POST', body: JSON.stringify({ sujet, message }),
  }),
  annoterCandidature: (id, donnees) => authFetch(`${ACC_BASE}/candidatures/${id}/`, {
    method: 'PATCH', body: JSON.stringify(donnees),
  }),
  // --- BARÈME DES GRADES ---------------------------------------------------
  // Les seuils se règlent depuis l'administration : ce sont des décisions de
  // politique interne, elles n'ont pas à passer par une livraison.
  getGrades: () => authFetch(`${ACC_BASE}/grades/`),
  creerGrade: (donnees) => authFetch(`${ACC_BASE}/grades/`, {
    method: 'POST', body: JSON.stringify(donnees),
  }),
  majGrade: (id, donnees) => authFetch(`${ACC_BASE}/grades/${id}/`, {
    method: 'PATCH', body: JSON.stringify(donnees),
  }),
  supprimerGrade: (id) => authFetch(`${ACC_BASE}/grades/${id}/`, { method: 'DELETE' }),

  deciderCandidature: (id, decision, motif = '') =>
    authFetch(`${ACC_BASE}/candidatures/${id}/decision/`, {
      method: 'POST', body: JSON.stringify({ decision, motif }),
    }),

  // --- REFERENTIEL STRUCTURAL ---
  // Lecture publique (le questionnaire du diagnostic en a besoin) ; ecritures protegees (JWT admin).
  getStructure: (domaineId = '') => {
    const query = domaineId ? `?domaine_id=${encodeURIComponent(domaineId)}` : '';
    return fetch(`${DJANGO_BASE_URL}/referentiel/structure/${query}`).then(handleResponse);
  },
  // Les domaines du référentiel, pour rattacher un module de formation.
  // C'est la même liste que celle du diagnostic : un module se rattache à
  // un domaine EXISTANT, il n'en invente pas.
  getDomaines: () => authFetch(`${DJANGO_BASE_URL}/referentiel/domaine/`),

  saveEntity: (type, data) => {
    const endpoints = { domaines: 'domaine', subdomains: 'sousdomaine', questions: 'question' };
    const method = data.id ? 'PUT' : 'POST';
    const url = data.id
      ? `${DJANGO_BASE_URL}/referentiel/${endpoints[type]}/${data.id}/`
      : `${DJANGO_BASE_URL}/referentiel/${endpoints[type]}/`;
    return authFetch(url, { method, body: JSON.stringify(data) });
  },
  deleteEntity: (type, id) => {
    const endpoints = { domaines: 'domaine', subdomains: 'sousdomaine', questions: 'question' };
    return authFetch(`${DJANGO_BASE_URL}/referentiel/${endpoints[type]}/${id}/`, {
      method: 'DELETE'
    });
  },
  // Persistance de l'ordre drag-and-drop des domaines.
  reorderDomaines: (orderedIds) => authFetch(`${DJANGO_BASE_URL}/referentiel/domaine/reorder/`, {
    method: 'POST',
    body: JSON.stringify({ ordered_ids: orderedIds })
  }),
  // Persistance de l'ordre drag-and-drop des sous-domaines d'un domaine.
  reorderSousDomaines: (domaineId, orderedIds) => authFetch(`${DJANGO_BASE_URL}/referentiel/sousdomaine/reorder/`, {
    method: 'POST',
    body: JSON.stringify({ domaine_id: domaineId, ordered_ids: orderedIds })
  }),

  // --- INTELLIGENCE STRATEGIQUE (JWT admin) ---
  getIntelligence: () => authFetch(`${DJANGO_BASE_URL}/diagnostics/intelligence/list/`),
  saveIntelligence: (data) => authFetch(`${DJANGO_BASE_URL}/diagnostics/intelligence/manage/${data.id ? data.id + '/' : ''}`, {
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data)
  }),
  deleteIntelligence: (id) => authFetch(`${DJANGO_BASE_URL}/diagnostics/intelligence/manage/${id}/`, { method: 'DELETE' }),

  getActionsBase: () => authFetch(`${DJANGO_BASE_URL}/diagnostics/actions/list/`),
  saveActionBase: (data) => authFetch(`${DJANGO_BASE_URL}/diagnostics/actions/manage/${data.id ? data.id + '/' : ''}`, {
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data)
  }),
  deleteActionBase: (id) => authFetch(`${DJANGO_BASE_URL}/diagnostics/actions/manage/${id}/`, { method: 'DELETE' }),

  getLivrables: () => authFetch(`${DJANGO_BASE_URL}/diagnostics/livrables/list/`),
  saveLivrable: (data) => authFetch(`${DJANGO_BASE_URL}/diagnostics/livrables/manage/${data.id ? data.id + '/' : ''}`, {
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data)
  }),
  deleteLivrable: (id) => authFetch(`${DJANGO_BASE_URL}/diagnostics/livrables/manage/${id}/`, { method: 'DELETE' }),

  getNiveaux: () => authFetch(`${DJANGO_BASE_URL}/diagnostics/niveaux/list/`),
  saveNiveau: (data) => authFetch(`${DJANGO_BASE_URL}/diagnostics/niveaux/manage/${data.id ? data.id + '/' : ''}`, {
    method: data.id ? 'PUT' : 'POST',
    body: JSON.stringify(data)
  }),
  deleteNiveau: (id) => authFetch(`${DJANGO_BASE_URL}/diagnostics/niveaux/manage/${id}/`, { method: 'DELETE' }),

  // --- STATS & ORGS (JWT equipe) ---
  getOrganisations: () => authFetch(`${DJANGO_BASE_URL}/users/organisations/`),
  deleteOrganisation: (id) => authFetch(`${DJANGO_BASE_URL}/users/organisations/${id}/`, { method: 'DELETE' }),
  getGlobalStats: () => authFetch(`${DJANGO_BASE_URL}/diagnostics/stats/`),

  // Le tableau de bord du modele : huit blocs, un seul appel, pour que
  // les chiffres d'une meme page viennent du meme instant.
  // `periode` vaut 'mois', 'trim' ou '12mois'.
  getTableauDeBord: (periode = '12mois') =>
    authFetch(`${DJANGO_BASE_URL}/diagnostics/tableau-de-bord/?periode=${encodeURIComponent(periode)}`),
  getDiagnosticByOrg: (id) => authFetch(`${DJANGO_BASE_URL}/diagnostics/by-org/${id}/`),

  // --- GESTION ADMINS ---
  getAdmins: () => authFetch(`${DJANGO_BASE_URL}/users/manage/`),
  getAdminById: (id) => authFetch(`${DJANGO_BASE_URL}/users/manage/${id}/`),
  // `email` est OBLIGATOIRE : aucun mot de passe n'est cree, le compte
  // s'ouvre par un lien a usage unique envoye a cette adresse. Le serveur
  // refuse la creation sans elle plutot que de fabriquer un compte que
  // personne ne pourrait ouvrir.
  createAdmin: (data) => authFetch(`${DJANGO_BASE_URL}/users/manage/`, {
    method: 'POST',
    body: JSON.stringify({
      username: data.username,
      role: data.role || 'ANALYSTE',
      email: data.email || '',
    }),
  }),
  // La session ouverte par le lien de premiere connexion sert de preuve :
  // le formulaire ne redemande aucun mot de passe. L'appel passe par
  // `authFetch` pour transporter le jeton, sans quoi le serveur retomberait
  // sur l'ancienne verification et refuserait.
  completeProfile: (data) => authFetch(`${DJANGO_BASE_URL}/users/complete-profile/`, {
    method: 'POST',
    body: JSON.stringify({
      username: data.username,
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      new_password: data.new_password,
    })
  }).then((reponse) => {
    // Le mot de passe vient de changer : les jetons sont renouveles pour que
    // la session en cours reste valide au lieu d'expirer sans prevenir.
    if (reponse.access) localStorage.setItem('asso_acc_token', reponse.access);
    if (reponse.refresh) localStorage.setItem('asso_acc_refresh', reponse.refresh);
    return reponse;
  }),
  toggleAdminStatus: (id, active) => authFetch(`${DJANGO_BASE_URL}/users/manage/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: active }),
  }),
  // Reinitialise le mot de passe : le nouveau est genere cote serveur et
  // envoye par e-mail brande — il ne transite jamais par le navigateur.
  resetAdminPassword: (id) => authFetch(`${DJANGO_BASE_URL}/users/manage/${id}/reset-password/`, {
    method: 'POST',
  }),
  deleteAdmin: (id) => authFetch(`${DJANGO_BASE_URL}/users/manage/${id}/`, { method: 'DELETE' }),
  updateAdminProfile: (id, data) => authFetch(`${DJANGO_BASE_URL}/users/manage/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({
      username: data.username,
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
    }),
  }),

  // --- PAIEMENTS (JWT equipe) ---
  getPayments: () => authFetch(`${DJANGO_BASE_URL}/payments/list/`),

  // --- MODULE ACCOMPAGNEMENT ---

  // Projets
  getProjets: (statut) => authFetch(`${ACC_BASE}/projets/${statut ? `?statut=${statut}` : ''}`),
  getProjet: (id) => authFetch(`${ACC_BASE}/projets/${id}/`),
  createProjet: (data) => authFetch(`${ACC_BASE}/projets/`, { method: 'POST', body: JSON.stringify(data) }),
  validerProjet: (id, coachId) => authFetch(`${ACC_BASE}/projets/${id}/valider/`, { method: 'POST', body: JSON.stringify({ coach_id: coachId }) }),
  annulerProjet: (id) => authFetch(`${ACC_BASE}/projets/${id}/annuler/`, { method: 'POST' }),
  updateProjet: (id, data) => authFetch(`${ACC_BASE}/projets/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  envoyerIdentifiants: (id) => authFetch(`${ACC_BASE}/projets/${id}/envoyer-identifiants/`, { method: 'POST' }),

  // Diagnostics accompagnement
  getDiagnosticsProjet: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/diagnostics/`),
  cloturerDiagnostic: (diagId) => authFetch(`${ACC_BASE}/diagnostics/${diagId}/cloturer/`, { method: 'POST' }),
  // Qui a répondu, qui manque, et le questionnaire du coach est-il ouvert
  getEtatReponses: (diagId) => authFetch(`${ACC_BASE}/diagnostics/${diagId}/etat-reponses/`),
  // Le regard du coach : seuls question_id et reponse_id partent, le serveur
  // relit les points dans le référentiel.
  repondreDiagnostic: (diagId, reponses) => authFetch(`${ACC_BASE}/diagnostics/${diagId}/reponses/`, { method: 'POST', body: JSON.stringify({ reponses }) }),
  // Qui répond à quel domaine (dirigeants, membres)
  reglerDomaine: (diagId, data) => authFetch(`${ACC_BASE}/diagnostics/${diagId}/configurations/`, { method: 'POST', body: JSON.stringify(data) }),
  // Le diagnostic initial et le final sont lances par le coach ; le
  // mi-parcours s'ouvre seul a sa date (voir parcours_diagnostic.py).
  getLancementDiagnostic: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/diagnostics/?avec_lancement=1`),
  lancerDiagnostic: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/diagnostics/lancer/`, { method: 'POST' }),
  // La formule d'un projet et l'etat de ses avantages.
  getFormuleProjet: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/formule/`),
  fixerFormuleProjet: (projetId, formule) => authFetch(`${ACC_BASE}/projets/${projetId}/formule/`, {
    method: 'POST', body: JSON.stringify({ formule }),
  }),
  getResultatsDiagnostic: (diagId) => authFetch(`${ACC_BASE}/diagnostics/${diagId}/resultats/`),

  // Coaches
  getCoaches: () => authFetch(`${ACC_BASE}/coaches/`),
  // La page « Coaches et mentors » : les comptes désactivés compris, pour
  // pouvoir les réactiver (les listes de choix n'ont que les actifs).
  getIntervenants: () => authFetch(`${ACC_BASE}/coaches/?tous=1`),
  createCoach: (data) => authFetch(`${ACC_BASE}/coaches/`, { method: 'POST', body: JSON.stringify(data) }),
  updateCoach: (id, data) => authFetch(`${ACC_BASE}/coaches/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCoach: (id) => authFetch(`${ACC_BASE}/coaches/${id}/`, { method: 'DELETE' }),
  // Tant que l'intervenant n'a pas choisi son mot de passe : un courriel
  // perdu ne doit pas le laisser a la porte.
  renvoyerLienCoach: (id) => authFetch(`${ACC_BASE}/coaches/${id}/renvoyer-lien/`, { method: 'POST' }),

  // Documents
  getDocuments: (projetId) => authFetch(`${ACC_BASE}/documents/?projet_id=${projetId}`),
  // Tous les dossiers d'une organisation d'un coup, pour sa fiche.
  // Les pieces demandees a l'organisation, et le depot a sa place.
  getDocumentsDemandes: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/documents-demandes/`),
  demanderDocument: (projetId, data) => authFetch(`${ACC_BASE}/projets/${projetId}/documents-demandes/`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  supprimerDemandeDocument: (id) => authFetch(`${ACC_BASE}/documents-demandes/${id}/`, { method: 'DELETE' }),
  deposerDocument: (formData) => authFetch(`${ACC_BASE}/documents/`, { method: 'POST', body: formData }),
  // Le fichier lui-même, pour l'aperçu : /media/ n'est pas lisible d'un autre domaine.
  fichierDocument: (id) => authFetchBlob(`${ACC_BASE}/documents/${id}/fichier/`),
  getDocumentsOrganisation: (orgId) => authFetch(`${ACC_BASE}/documents/?organisation_id=${encodeURIComponent(orgId)}`),
  validerDocument: (id, statut, commentaire) => authFetch(`${ACC_BASE}/documents/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({ statut, commentaire_coach: commentaire }),
  }),

  // Actions
  getActions: (projetId) => authFetch(`${ACC_BASE}/actions/?projet_id=${projetId}`),
  updateAction: (id, data) => authFetch(`${ACC_BASE}/actions/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  cocherLivrable: (actionId, livrableId, coche) => authFetch(`${ACC_BASE}/actions/${actionId}/livrables/${livrableId}/`, {
    method: 'PATCH', body: JSON.stringify({ coche }) }),
  createAction: (data) => authFetch(`${ACC_BASE}/actions/`, { method: 'POST', body: JSON.stringify(data) }),
  deleteAction: (id) => authFetch(`${ACC_BASE}/actions/${id}/`, { method: 'DELETE' }),

  // Suivi du coach : dossier, lecture du diagnostic, plan, seances, echanges, rapports
  getTableauCoach: () => authFetch(`${ACC_BASE}/coach/tableau-de-bord/`),
  getDossierProjet: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/dossier/`),
  getInterpretation: (diagId) => authFetch(`${ACC_BASE}/diagnostics/${diagId}/interpretation/`),
  programmerPlan: (projetId, data) => authFetch(`${ACC_BASE}/projets/${projetId}/plan/programmer/`, { method: 'POST', body: JSON.stringify(data) }),
  getSeances: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/seances/`),
  createSeance: (projetId, data) => authFetch(`${ACC_BASE}/projets/${projetId}/seances/`, { method: 'POST', body: JSON.stringify(data) }),
  updateSeance: (id, data) => authFetch(`${ACC_BASE}/seances/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteSeance: (id) => authFetch(`${ACC_BASE}/seances/${id}/`, { method: 'DELETE' }),
  // Le fichier .ics exige l'en-tete d'authentification : un simple lien ne le porte pas.
  // Le rapport PDF d'un diagnostic d'accompagnement clos.
  telechargerRapportDiag: async (diagId) => {
    const blob = await authFetchBlob(`${ACC_BASE}/diagnostics/${diagId}/rapport/`);
    const url = URL.createObjectURL(blob);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = `rapport-diagnostic-${diagId}.pdf`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  telechargerIcs: async (id) => {
    const res = await fetch(`${ACC_BASE}/seances/${id}/ics/`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Le téléchargement n'a pas abouti. ${phraseDErreur(res.status)}`);
    const url = URL.createObjectURL(await res.blob());
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = `seance-asso-pro-${id}.ics`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  getMessagesProjet: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/messages/`),
  envoyerMessageProjet: (projetId, contenu) => authFetch(`${ACC_BASE}/projets/${projetId}/messages/`, { method: 'POST', body: JSON.stringify({ contenu }) }),
  getMessagesNonLus: () => authFetch(`${ACC_BASE}/messages/non-lus/`),
  getRapports: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/rapports/`),
  createRapport: (projetId, data) => authFetch(`${ACC_BASE}/projets/${projetId}/rapports/`, { method: 'POST', body: JSON.stringify(data) }),
  updateRapport: (id, data) => authFetch(`${ACC_BASE}/rapports/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteRapport: (id) => authFetch(`${ACC_BASE}/rapports/${id}/`, { method: 'DELETE' }),
  envoyerRapport: (id, data = {}) => authFetch(`${ACC_BASE}/rapports/${id}/envoyer/`, { method: 'POST', body: JSON.stringify(data) }),

  // Formations du coach sur l'Academy (SSO + inscription au clic)
  academySso: (moduleId) => authFetch(`${ACC_BASE}/formation/academy/sso/`, { method: 'POST', body: JSON.stringify(moduleId ? { module_id: moduleId } : {}) }),
  academyProgression: () => authFetch(`${ACC_BASE}/formation/academy/progression/`),

  // Formation
  getModules: () => authFetch(`${ACC_BASE}/formation/modules/`),
  // Le catalogue VIVANT de l'Academy : les cours tels qu'ils existent
  // là-bas. Importer depuis cette liste évite de retaper la référence, qui
  // est la clé par laquelle l'avancement remonte.
  getCatalogueAcademy: () => authFetch(`${ACC_BASE}/formation/academy/catalogue/`),

  // Le gabarit dans lequel un certificat sera composé, un par nature de
  // projet. Le corps est un FormData : `authFetch` laisse alors le
  // navigateur poser lui-même la frontière multipart.
  getModelesCertificat: () => authFetch(`${ACC_BASE}/formation/modeles-certificat/`),
  deposerModeleCertificat: (formData) =>
    authFetch(`${ACC_BASE}/formation/modeles-certificat/`, { method: 'POST', body: formData }),
  // Academy : ouvrir l'acces d'un membre a un cours Moodle (reserve aux admins).
  createModule: (data) => authFetch(`${ACC_BASE}/formation/modules/`, { method: 'POST', body: JSON.stringify(data) }),
  updateModule: (id, data) => authFetch(`${ACC_BASE}/formation/modules/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteModule: (id) => authFetch(`${ACC_BASE}/formation/modules/${id}/`, { method: 'DELETE' }),

  // Kit ASSO PRO
  getKit: () => authFetch(`${ACC_BASE}/kit/`),
  getSoumissionsKit: (projetId) => authFetch(`${ACC_BASE}/kit/soumissions/${projetId ? `?projet_id=${projetId}` : ''}`),
  validerSoumissionKit: (id, statut, commentaire) => authFetch(`${ACC_BASE}/kit/soumissions/${id}/valider/`, {
    method: 'PATCH',
    body: JSON.stringify({ statut, commentaire }),
  }),

  // Notifications accompagnement
  getAccNotifications: () => authFetch(`${ACC_BASE}/notifications/`),
  marquerAccNotifLue: (id) => authFetch(`${ACC_BASE}/notifications/${id}/lire/`, { method: 'PATCH' }),
  marquerToutesLues: () => authFetch(`${ACC_BASE}/notifications/lire-toutes/`, { method: 'POST' }),

  // Dashboard accompagnement
  getDashboardAcc: () => authFetch(`${ACC_BASE}/dashboard/`),

  // Demandes de service
  getDemandes: (query = '') => authFetch(`${ACC_BASE}/demandes/${query ? `?${query}` : ''}`),
  getDemandeDetail: (id) => authFetch(`${ACC_BASE}/demandes/${id}/`),
  updateDemande: (id, data) => authFetch(`${ACC_BASE}/demandes/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  accepterDemande: (id, data) => authFetch(`${ACC_BASE}/demandes/${id}/accepter/`, { method: 'POST', body: JSON.stringify(data) }),
  repondreDemande: (id, message) => authFetch(`${ACC_BASE}/demandes/${id}/repondre/`, { method: 'POST', body: JSON.stringify({ message }) }),

  // Formation & Certification — admin
  getModulesFormationProjet: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/modules-formation/`),
  saveModulesFormationProjet: (projetId, moduleIds) => authFetch(`${ACC_BASE}/projets/${projetId}/modules-formation/`, { method: 'POST', body: JSON.stringify({ module_ids: moduleIds }) }),
  getStatutFormation: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/statut-formation/`),
  getExamenConfig: (typeProjet) => authFetch(`${ACC_BASE}/formation/examen/${typeProjet}/`),
  saveExamenConfig: (typeProjet, data) => authFetch(`${ACC_BASE}/formation/examen/${typeProjet}/`, { method: 'POST', body: JSON.stringify(data) }),
  ajouterQuestionExamen: (typeProjet, data) => authFetch(`${ACC_BASE}/formation/examen/${typeProjet}/questions/`, { method: 'POST', body: JSON.stringify(data) }),
  updateQuestionExamen: (questionId, data) => authFetch(`${ACC_BASE}/formation/examen/questions/${questionId}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteQuestionExamen: (questionId) => authFetch(`${ACC_BASE}/formation/examen/questions/${questionId}/`, { method: 'DELETE' }),
  getConditionsCertif: (typeProjet) => authFetch(`${ACC_BASE}/formation/conditions/${typeProjet}/`),
  updateConditionsCertif: (typeProjet, data) => authFetch(`${ACC_BASE}/formation/conditions/${typeProjet}/`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Contrats
  getContrat: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/contrat/`),
  uploadContrat: (projetId, formData) => fetch(`${ACC_BASE}/projets/${projetId}/contrat/`, {
    method: 'POST',
    headers: { ...authHeaders() },
    body: formData,
  }).then(handleResponse),
  updateContrat: (projetId, formData) => fetch(`${ACC_BASE}/projets/${projetId}/contrat/`, {
    method: 'PATCH',
    headers: { ...authHeaders() },
    body: formData,
  }).then(handleResponse),
  validerContrat: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/contrat/valider/`, { method: 'POST' }),

  // Participants
  getParticipants: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/participants/`),
  createParticipant: (projetId, data) => authFetch(`${ACC_BASE}/projets/${projetId}/participants/`, { method: 'POST', body: JSON.stringify(data) }),
  updateParticipant: (participantId, data) => authFetch(`${ACC_BASE}/participants/${participantId}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteParticipant: (participantId) => authFetch(`${ACC_BASE}/participants/${participantId}/`, { method: 'DELETE' }),
  // Les modules que le coach attribue à un participant
  // La formation de l'équipe d'un projet : domaines par personne, avancement,
  // attestations (`accompagnement/vues_equipe.py`).
  getComptesOrganisation: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/comptes/`),
  setPrincipalCompte: (projetId, userId) => authFetch(`${ACC_BASE}/projets/${projetId}/comptes/`, { method: 'PATCH', body: JSON.stringify({ user_id: userId, principal: true }) }),
  retirerCompte: (projetId, userId) => authFetch(`${ACC_BASE}/projets/${projetId}/comptes/`, { method: 'DELETE', body: JSON.stringify({ user_id: userId }) }),
  setRoleCompte: (projetId, userId, role) => authFetch(`${ACC_BASE}/projets/${projetId}/comptes/`, { method: 'PATCH', body: JSON.stringify({ user_id: userId, role }) }),
  getFormationEquipe: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/formation-equipe/`),
  getDomainesParticipant: (participantId) => authFetch(`${ACC_BASE}/participants/${participantId}/domaines/`),
  setDomainesParticipant: (participantId, domaineIds) => authFetch(`${ACC_BASE}/participants/${participantId}/domaines/`, { method: 'PUT', body: JSON.stringify({ domaine_ids: domaineIds }) }),
  telechargerAttestation: async (projetId, domaineId, participantId) => {
    const params = new URLSearchParams({ domaine: domaineId });
    if (participantId) params.set('personne', participantId);
    const blob = await authFetchBlob(`${ACC_BASE}/projets/${projetId}/attestation/?${params}`);
    const url = URL.createObjectURL(blob);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = `attestation-${domaineId}.pdf`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // Configurations domaines
  getConfigsDomaines: (diagId) => authFetch(`${ACC_BASE}/diagnostics/${diagId}/configurations/`),
  saveConfigDomaine: (diagId, data) => authFetch(`${ACC_BASE}/diagnostics/${diagId}/configurations/`, { method: 'POST', body: JSON.stringify(data) }),
  updateConfigDomaine: (configId, data) => authFetch(`${ACC_BASE}/configurations/${configId}/`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Certificats
  getCertificat: (projetId) => authFetch(`${ACC_BASE}/projets/${projetId}/certificat/`),
  uploadCertificat: (projetId, formData) => fetch(`${ACC_BASE}/projets/${projetId}/certificat/`, {
    method: 'POST',
    headers: { ...authHeaders() },
    body: formData,
  }).then(handleResponse),

  // --- CONTACT EXPERT / NOTIFICATIONS ---
  createExpertContactRequest: (data) => fetch(`${DJANGO_BASE_URL}/users/expert-contacts/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(handleResponse),
  getAdminNotifications: (adminId, limit = 15) => authFetch(
    `${DJANGO_BASE_URL}/users/admin-notifications/?admin_id=${encodeURIComponent(adminId)}&limit=${limit}`
  ),
  markAdminNotificationsRead: ({ adminId, notificationIds = [], markAll = false, notificationId = null }) => authFetch(
    `${DJANGO_BASE_URL}/users/admin-notifications/mark-read/`,
    {
      method: 'POST',
      body: JSON.stringify({
        admin_id: adminId,
        notification_ids: notificationIds,
        notification_id: notificationId,
        mark_all: markAll,
      }),
    }
  ),

  // Archivage d'une conversation. Le drapeau appartient a la notification de
  // CET administrateur : ranger sa boite ne vide pas celle des autres.
  archiverNotification: ({ adminId, notificationId, archiver = true }) => authFetch(
    `${DJANGO_BASE_URL}/users/admin-notifications/archiver/`,
    {
      method: 'POST',
      body: JSON.stringify({
        admin_id: adminId,
        notification_id: notificationId,
        archiver,
      }),
    }
  ),

  // --- RETOURS SUR L'OUTIL (probleme signale, suggestion) ---
  // Les categories viennent du serveur : les recopier cote interface aurait
  // cree une seconde liste, et le serveur refuserait la valeur proposee le
  // jour ou l'une des deux change.
  categoriesRetour: () => authFetch(`${DJANGO_BASE_URL}/users/retours/`),

  envoyerRetour: ({ nature, categorie, message, page, capture = null }) => {
    const corps = new FormData();
    corps.append('nature', nature);
    corps.append('categorie', categorie);
    corps.append('message', message);
    corps.append('page', page || '');
    if (capture) corps.append('capture', capture);
    return authFetch(`${DJANGO_BASE_URL}/users/retours/`, { method: 'POST', body: corps });
  },

  // --- REPONSE AUX MESSAGES DE CONTACT ---
  // Un document peut accompagner la reponse (recu, contrat). Sa presence fait
  // basculer l'envoi en `FormData` : un fichier ne se serialise pas en JSON.
  replyToContact: (contactRequestId, message, pieceJointe = null) => {
    const url = `${DJANGO_BASE_URL}/users/expert-contacts/${contactRequestId}/reply/`;
    if (!pieceJointe) {
      return authFetch(url, { method: 'POST', body: JSON.stringify({ message }) });
    }
    const corps = new FormData();
    corps.append('message', message);
    corps.append('piece_jointe', pieceJointe);
    return authFetch(url, { method: 'POST', body: corps });
  },

  // --- ANALYSE STATISTIQUE DU TABLEAU DE BORD ---
  // Dispersion, intervalles de confiance, tendance testee et correlation de
  // rangs sont calcules cote serveur : le navigateur n a jamais l ensemble des
  // donnees, et une moyenne sur un echantillon partiel est une moyenne fausse.
  getAnalytics: () => authFetch(`${DJANGO_BASE_URL}/diagnostics/analytics/`),

  // --- MOT DE PASSE PERSONNEL ---
  // Chacun change le sien. Auparavant, seul `AdminPasswordResetView`
  // existait : un administrateur reinitialisait celui d un autre, et le
  // nouveau transitait par e-mail.
  changerMonMotDePasse: (ancien, nouveau) => authFetch(
    `${DJANGO_BASE_URL}/users/me/mot-de-passe/`,
    { method: 'POST', body: JSON.stringify({
        ancien_mot_de_passe: ancien, nouveau_mot_de_passe: nouveau }) },
  ),

  // --- ZONES DU BACK-OFFICE ---
  // Le serveur declare ce que l utilisateur peut ouvrir ; l interface s y
  // conforme au lieu de coder les roles en dur. Les deux lisent la meme table,
  // ils ne peuvent donc plus diverger.
  // --- CATALOGUE DES FORMULES ---------------------------------------------
  // Le catalogue n'est plus fige dans le code : il se lit et se corrige ici.
  formulesAdmin: () => authFetch(`${ACC_BASE}/admin/formules/`),
  creerFormule: (donnees) => authFetch(`${ACC_BASE}/admin/formules/`, {
    method: 'POST', body: JSON.stringify(donnees),
  }),
  majFormule: (code, donnees) => authFetch(`${ACC_BASE}/admin/formules/${code}/`, {
    method: 'PATCH', body: JSON.stringify(donnees),
  }),
  supprimerFormule: (code) => authFetch(`${ACC_BASE}/admin/formules/${code}/`, {
    method: 'DELETE',
  }),

  mesZones: () => authFetch(`${DJANGO_BASE_URL}/users/me/zones/`),
  // La meme table lue dans l'autre sens : qui peut ouvrir quoi.
  matriceDesDroits: () => authFetch(`${DJANGO_BASE_URL}/users/zones/matrice/`),

  // Enregistre la matrice. Reserve au super administrateur -- le serveur
  // le verifie, l'interface se contente de masquer les cases.
  enregistrerMatriceDesDroits: (acces) =>
    authFetch(`${DJANGO_BASE_URL}/users/zones/matrice/`, {
      method: 'PUT',
      body: JSON.stringify({ acces }),
    }),

  // --- CMS : GESTION DES CONTENUS (SUPER_ADMIN) ---
  // Le schema decrit les sections editables ; l'interface se construit a partir
  // de lui, sans connaissance codee en dur des modeles cote serveur.
  cmsSchema: () => authFetch(`${CMS_BASE}/schema/`),

  cmsGetSection: (cle) => authFetch(`${CMS_BASE}/section/${cle}/`),
  cmsSaveSection: (cle, donnees) => authFetch(`${CMS_BASE}/section/${cle}/`, {
    method: 'PATCH',
    body: JSON.stringify(donnees),
  }),
  // Les televersements passent en multipart : le navigateur pose lui-meme la
  // frontiere du corps, il ne faut donc surtout pas fixer Content-Type.
  cmsSaveSectionFichiers: (cle, formData) => authFetch(`${CMS_BASE}/section/${cle}/`, {
    method: 'PATCH',
    body: formData,
  }),

  cmsGetCollection: (cle) => authFetch(`${CMS_BASE}/collection/${cle}/`),
  cmsCreerElement: (cle, donnees) => authFetch(`${CMS_BASE}/collection/${cle}/`, {
    method: 'POST',
    body: JSON.stringify(donnees),
  }),
  cmsCreerElementFichiers: (cle, formData) => authFetch(`${CMS_BASE}/collection/${cle}/`, {
    method: 'POST',
    body: formData,
  }),
  cmsModifierElement: (cle, id, donnees) => authFetch(`${CMS_BASE}/collection/${cle}/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(donnees),
  }),
  cmsModifierElementFichiers: (cle, id, formData) => authFetch(`${CMS_BASE}/collection/${cle}/${id}/`, {
    method: 'PATCH',
    body: formData,
  }),
  cmsSupprimerElement: (cle, id) => authFetch(`${CMS_BASE}/collection/${cle}/${id}/`, {
    method: 'DELETE',
  }),
  cmsReordonner: (cle, ordre) => authFetch(`${CMS_BASE}/collection/${cle}/reordonner/`, {
    method: 'POST',
    body: JSON.stringify({ ordre }),
  }),

  /* ══ Cycle de supervision ════════════════════════════════════════════
     Trois espaces, et la séparation n'est pas cosmétique :

       · « mes-documents » ne demande que d'être l'intervenant concerné ;
       · « supervision »  passe par la zone « Coaches et mentors » ;
       · « paiements »    par la zone du même nom, plus la séparation
                          validateur / payeur, vérifiée relevé par relevé.

     Hors périmètre, le serveur répond 404 et non 403 : confirmer
     l'existence d'un document dirait déjà quelque chose. Les écrans
     doivent donc traiter le 404 comme « rien à voir ici », pas comme une
     panne. */

  // ── Espace de l'intervenant ────────────────────────────────────────
  mesFiches: () => authFetch(`${ACC_BASE}/mes-documents/fiches/`),
  ouvrirMaFiche: (seanceId) =>
    authFetch(`${ACC_BASE}/mes-documents/fiches/seance/${seanceId}/`, { method: 'POST' }),
  maFiche: (id) => authFetch(`${ACC_BASE}/mes-documents/fiches/${id}/`),
  majMaFiche: (id, donnees) => authFetch(`${ACC_BASE}/mes-documents/fiches/${id}/`, {
    method: 'PATCH', body: JSON.stringify(donnees),
  }),
  validerMaFiche: (id) =>
    authFetch(`${ACC_BASE}/mes-documents/fiches/${id}/valider/`, { method: 'POST' }),
  // FormData : `authFetch` laisse le navigateur poser la frontière multipart.
  deposerScanFiche: (id, formData) =>
    authFetch(`${ACC_BASE}/mes-documents/fiches/${id}/depot/`, {
      method: 'POST', body: formData,
    }),
  transmettreMaFiche: (id) =>
    authFetch(`${ACC_BASE}/mes-documents/fiches/${id}/transmettre/`, { method: 'POST' }),
  urlPdfMaFiche: (id) => `${ACC_BASE}/mes-documents/fiches/${id}/document/`,

  mesReleves: () => authFetch(`${ACC_BASE}/mes-documents/releves/`),
  preparationReleve: (mois) =>
    authFetch(`${ACC_BASE}/mes-documents/releves/preparation/${mois ? `?mois=${mois}` : ''}`),
  // `ficheIds` : les seances que l'intervenant choisit de facturer.
  emettreReleve: (mois, ficheIds) => authFetch(`${ACC_BASE}/mes-documents/releves/`, {
    method: 'POST',
    body: JSON.stringify({ ...(mois ? { mois } : {}), ...(ficheIds ? { fiche_ids: ficheIds } : {}) }),
  }),
  deposerScanReleve: (id, formData) =>
    authFetch(`${ACC_BASE}/mes-documents/releves/${id}/depot/`, {
      method: 'POST', body: formData,
    }),
  transmettreMonReleve: (id) =>
    authFetch(`${ACC_BASE}/mes-documents/releves/${id}/transmettre/`, { method: 'POST' }),
  annulerMonReleve: (id, motif) =>
    authFetch(`${ACC_BASE}/mes-documents/releves/${id}/annuler/`, {
      method: 'POST', body: JSON.stringify({ motif }),
    }),
  urlPdfMonReleve: (id) => `${ACC_BASE}/mes-documents/releves/${id}/document/`,
  urlApercuMonReleve: (mois) =>
    `${ACC_BASE}/mes-documents/releves/apercu/${mois ? `?mois=${mois}` : ''}`,

  mesEvaluations: () => authFetch(`${ACC_BASE}/mes-documents/evaluations/`),
  urlPdfMonEvaluation: (id) => `${ACC_BASE}/mes-documents/evaluations/${id}/document/`,

  // ── Supervision ────────────────────────────────────────────────────
  supervisionFiches: (statut) =>
    authFetch(`${ACC_BASE}/supervision/fiches/${statut ? `?statut=${statut}` : ''}`),
  supervisionFiche: (id) => authFetch(`${ACC_BASE}/supervision/fiches/${id}/`),
  recevoirFiche: (id) =>
    authFetch(`${ACC_BASE}/supervision/fiches/${id}/reception/`, { method: 'POST' }),
  renvoyerFiche: (id, motif) =>
    authFetch(`${ACC_BASE}/supervision/fiches/${id}/correction/`, {
      method: 'POST', body: JSON.stringify({ motif }),
    }),
  urlPdfFicheSupervision: (id) => `${ACC_BASE}/supervision/fiches/${id}/document/`,
  urlScanFiche: (id) => `${ACC_BASE}/supervision/fiches/${id}/scan/`,

  supervisionReleves: (statut) =>
    authFetch(`${ACC_BASE}/supervision/releves/${statut ? `?statut=${statut}` : ''}`),
  validerReleve: (id) =>
    authFetch(`${ACC_BASE}/supervision/releves/${id}/validation/`, { method: 'POST' }),
  renvoyerReleve: (id, motif) =>
    authFetch(`${ACC_BASE}/supervision/releves/${id}/correction/`, {
      method: 'POST', body: JSON.stringify({ motif }),
    }),
  urlPdfReleveSupervision: (id) => `${ACC_BASE}/supervision/releves/${id}/document/`,

  supervisionEvaluations: () => authFetch(`${ACC_BASE}/supervision/evaluations/`),
  ouvrirEvaluation: (donnees) => authFetch(`${ACC_BASE}/supervision/evaluations/`, {
    method: 'POST', body: JSON.stringify(donnees),
  }),
  evaluation: (id) => authFetch(`${ACC_BASE}/supervision/evaluations/${id}/`),
  majEvaluation: (id, donnees) => authFetch(`${ACC_BASE}/supervision/evaluations/${id}/`, {
    method: 'PATCH', body: JSON.stringify(donnees),
  }),
  finaliserEvaluation: (id) =>
    authFetch(`${ACC_BASE}/supervision/evaluations/${id}/finaliser/`, { method: 'POST' }),
  mesuresTrimestre: (intervenantId, annee, trimestre) =>
    authFetch(`${ACC_BASE}/supervision/mesures/${intervenantId}/`
              + `?annee=${annee}&trimestre=${trimestre}`),

  /* ── La signature électronique ──────────────────────────────────────
     Chacun enregistre LA sienne, une fois. Elle ne quitte jamais le
     stockage privé du serveur : il n'existe pas d'adresse permettant de
     demander celle de quelqu'un d'autre. */
  maSignature: () => authFetch(`${ACC_BASE}/ma-signature/`),
  // `corps` : soit un FormData (photo), soit { trace } (canvas).
  enregistrerMaSignature: (corps) => authFetch(`${ACC_BASE}/ma-signature/`, {
    method: 'POST',
    body: corps instanceof FormData ? corps : JSON.stringify(corps),
  }),
  supprimerMaSignature: () =>
    authFetch(`${ACC_BASE}/ma-signature/`, { method: 'DELETE' }),
  // En blob, pas en URL : une balise `<img>` n'envoie pas le jeton, et
  // l'image est servie par une vue qui l'exige.
  imageMaSignature: () => authFetchBlob(`${ACC_BASE}/ma-signature/image/`),

  /* **Jamais de signature sans aperçu.** Le GET rend le document avec la
     signature incrustée, SANS rien enregistrer ; c'est le POST qui
     signe. Les deux portent sur la même adresse, à dessein : les séparer
     inviterait à montrer l'un et signer l'autre. */
  apercuSignature: (genre, id, bloc) =>
    authFetchBlob(`${ACC_BASE}/documents/${genre}/${id}/signature/?bloc=${bloc}`),
  signerDocument: (genre, id, bloc) =>
    authFetch(`${ACC_BASE}/documents/${genre}/${id}/signature/`, {
      method: 'POST', body: JSON.stringify({ bloc }),
    }),
  retirerLeScan: (genre, id) =>
    authFetch(`${ACC_BASE}/documents/${genre}/${id}/retirer-le-scan/`,
              { method: 'POST' }),

  /* ── Le suivi de l'argent ───────────────────────────────────────────
     Aucun de ces appels ne fait sortir un franc. Ils matérialisent ce qui
     est dû, prennent acte de ce qui a été versé DEHORS, et tiennent un
     solde. Le versement lui-même se fait par virement, en espèces ou par
     mobile money, et la plateforme l'enregistre. */

  // Côté intervenant : son solde, ses demandes.
  mesRetraits: () => authFetch(`${ACC_BASE}/mes-documents/retraits/`),
  demanderUnRetrait: (montant, message = '') =>
    authFetch(`${ACC_BASE}/mes-documents/retraits/`, {
      method: 'POST', body: JSON.stringify({ montant, message }),
    }),
  // La facture est un fichier privé : une balise n'enverrait pas le jeton.
  factureRetrait: (id) => authFetchBlob(`${ACC_BASE}/retraits/${id}/facture/`),

  // Côté administration : autoriser, refuser, verser.
  retraitsATraiter: (statut) =>
    authFetch(`${ACC_BASE}/retraits/${statut ? `?statut=${statut}` : ''}`),
  traiterUnRetrait: (id, action, donnees = {}) =>
    authFetch(`${ACC_BASE}/retraits/${id}/${action}/`, {
      method: 'POST', body: JSON.stringify(donnees),
    }),

  // Côté association : l'échéancier et ses règlements.
  echeancesProjet: (projetId) =>
    authFetch(`${ACC_BASE}/projets/${projetId}/echeances/`),
  enregistrerUnReglement: (echeanceId, donnees) =>
    authFetch(`${ACC_BASE}/echeances/${echeanceId}/reglement/`, {
      method: 'POST', body: JSON.stringify(donnees),
    }),
  recuEcheance: (echeanceId) =>
    authFetchBlob(`${ACC_BASE}/echeances/${echeanceId}/recu/`),

  // ── Paiement ───────────────────────────────────────────────────────
  relevesAPayer: () => authFetch(`${ACC_BASE}/paiements-intervenants/releves/`),
  payerReleve: (id, donnees) =>
    authFetch(`${ACC_BASE}/paiements-intervenants/releves/${id}/paiement/`, {
      method: 'POST', body: JSON.stringify(donnees),
    }),
};
